import { map, Observable } from 'rxjs';

/**
 * Makes object properties case-insensitive for reads and writes.
 * It wraps provided object (dto) or array of objects (dtos) and all nested objects in Proxies that allow case-insensitive property read/write access.
 * myObj.myProperty === myObj.MYPROPERTY === myObj.myproperty
 *
 * @example:
 * ```
 * const data = makeCaseInsensitivePropertyAccess({
 *   Value: [
 *     { Name: 'Alice', Address: { City: 'New York' } },
 *     { NAME: 'Bob', ADDRESS: { CITY: 'Tokyo' } },
 *   ],
 * });
 *
 * console.log(data.value[0].address.city); // ✅ New York
 * console.log(data.VALUE[1].ADDRESS.CITY); // ✅ Tokyo
 *
 * data.value[0].ADDRESS.CITY = 'Seattle';
 * console.log(data.Value[0].Address.City); // ✅ Seattle
 *
 * console.log(JSON.stringify(data, null, 2)); // ✅ Works without errors
 * ```
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
export function makeCaseInsensitivePropertyAccess<T>(obj: T): T {
  const visited = new WeakSet<any>();

  const wrap = (target: any): any => {
    if (target === null || typeof target !== 'object') return target;
    if (visited.has(target)) return target;
    visited.add(target);
    /* eslint-enable @typescript-eslint/no-explicit-any */
    if (Array.isArray(target)) {
      return target.map((item) => wrap(item));
    }

    return new Proxy(target, {
      get(t, prop: string | symbol, receiver) {
        // Handle symbols & primitive conversions
        if (prop === Symbol.toPrimitive) {
          // delegate to JSON/string representation
          return (hint: string) => {
            if (hint === 'number') return NaN;
            if (hint === 'string') return JSON.stringify(t);
            return t;
          };
        }
        if (prop === 'toString') {
          return () => JSON.stringify(t);
        }
        if (prop === 'valueOf') {
          return () => t;
        }
        if (typeof prop !== 'string') {
          return Reflect.get(t, prop, receiver);
        }

        const key = Object.keys(t).find(
          (k) => k.toLowerCase() === prop.toLowerCase()
        );
        const value = key ? t[key] : undefined;
        return wrap(value);
      },

      set(t, prop: string | symbol, value) {
        if (typeof prop !== 'string') {
          return Reflect.set(t, prop, value);
        }
        const key = Object.keys(t).find(
          (k) => k.toLowerCase() === prop.toLowerCase()
        );
        t[key ?? prop] = value;
        return true;
      },

      has(t, prop: string | symbol) {
        if (typeof prop !== 'string') {
          return Reflect.has(t, prop);
        }
        return Object.keys(t).some(
          (k) => k.toLowerCase() === prop.toLowerCase()
        );
      },
    });
  };

  return wrap(obj);
}

/**
 * Makes object properties case-insensitive for reads and writes.
 * Recursively converts objects or arrays into case-insensitive proxies.
 * @example
 * ```
 * this.httpClient.get<MyDto>(url).pipe(toCaseInsensitive());
 * // Access is case-insensitive:
 * myObj.myProperty === myObj.MYPROPERTY === myObj.myproperty
 * ```
 */
export function toCaseInsensitivePropertiesAccess<T>() {
  return (source: Observable<T>): Observable<T> => {
    return source.pipe(
      map((response) => makeCaseInsensitivePropertyAccess(response) as T)
    );
  };
}
